import { useState } from "react";
import { Check, Trash2, Moon, Plus, ListTodo } from "lucide-react";
import { VoiceButton } from "./VoiceButton";

const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Brain-dump task inbox with snooze. Offloads working memory.
export const TasksInbox = ({ tasks = [], onCreate, onComplete, onSnooze, onDelete }) => {
  const [text, setText] = useState("");
  const [snoozeFor, setSnoozeFor] = useState(null); // task id showing date picker

  const add = () => {
    const t = text.trim();
    if (!t) return;
    onCreate(t, "manual");
    setText("");
  };

  return (
    <div data-testid="tasks-inbox">
      <div className="flex items-center gap-2 mb-3">
        <ListTodo size={16} style={{ color: "var(--dash-text-muted)" }} />
        <h3 className="font-body text-xs tracking-[0.2em] uppercase" style={{ color: "var(--dash-text-muted)" }}>
          Tasks ({tasks.length})
        </h3>
      </div>

      {/* Add row */}
      <div className="flex items-center gap-2 mb-3 border p-2" style={{ backgroundColor: "var(--dash-card)", borderColor: "var(--dash-border)" }}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a task, or use the mic…"
          data-testid="task-add-input"
          className="flex-1 bg-transparent outline-none font-body text-sm"
          style={{ color: "var(--dash-text)" }}
        />
        <VoiceButton
          onText={(t) => setText((p) => (p ? p + " " : "") + t.trim())}
          size={16}
          testId="task-add-voice-btn"
          title="Dictate task"
          className="text-zinc-400"
        />
        <button onClick={add} data-testid="task-add-btn" className="flex items-center gap-1 px-3 py-1.5 text-xs uppercase font-body bg-[#3b82f6] text-black border-none cursor-pointer">
          <Plus size={14} /> Add
        </button>
      </div>

      {/* List */}
      {tasks.length === 0 ? (
        <p className="font-body text-sm" style={{ color: "var(--dash-text-dim)" }}>Nothing on the list — capture a thought above.</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <div key={task.id} data-testid={`task-row-${task.id}`}
              className="flex items-center gap-3 p-3 border" style={{ backgroundColor: "var(--dash-card)", borderColor: "var(--dash-border)" }}>
              <button onClick={() => onComplete(task.id)} data-testid={`task-complete-${task.id}`}
                title="Mark done"
                className="w-7 h-7 flex items-center justify-center border border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors cursor-pointer bg-transparent flex-shrink-0">
                <Check size={14} />
              </button>
              <span className="flex-1 font-body text-sm" style={{ color: "var(--dash-text)" }}>{task.text}</span>
              {snoozeFor === task.id ? (
                <input type="date" autoFocus data-testid={`task-snooze-date-${task.id}`}
                  onChange={(e) => { if (e.target.value) { onSnooze(task.id, e.target.value); setSnoozeFor(null); } }}
                  onBlur={() => setSnoozeFor(null)}
                  className="bg-transparent border text-xs p-1 font-body cursor-pointer"
                  style={{ color: "var(--dash-text)", borderColor: "var(--dash-border)" }} />
              ) : (
                <>
                  <button onClick={() => onSnooze(task.id, tomorrowStr())} data-testid={`task-snooze-tomorrow-${task.id}`}
                    title="Snooze to tomorrow"
                    className="px-2 py-1 text-[10px] uppercase font-body border text-zinc-400 hover:text-white transition-colors cursor-pointer bg-transparent flex-shrink-0"
                    style={{ borderColor: "var(--dash-border)" }}>
                    Tmrw
                  </button>
                  <button onClick={() => setSnoozeFor(task.id)} data-testid={`task-snooze-${task.id}`}
                    title="Snooze to a date"
                    className="text-zinc-500 hover:text-white transition-colors cursor-pointer bg-transparent border-none flex-shrink-0">
                    <Moon size={15} />
                  </button>
                </>
              )}
              <button onClick={() => onDelete(task.id)} data-testid={`task-delete-${task.id}`}
                title="Delete"
                className="text-zinc-600 hover:text-red-400 transition-colors cursor-pointer bg-transparent border-none flex-shrink-0">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TasksInbox;
